import React, { useRef, useEffect } from 'react';
import type {
	EditWidgetRowProps,
	WidgetDataSourceProps
} from 'App/components/hooks/integrations/externalIntegrationTypes';
import { Input, Flex, Text, ButtonSelect, Textarea } from '@upsales/components';
import './UiWidgetIframe.scss';
import { handleIntegrationIframeMessage } from 'App/helpers/uiElementsHelper';
import WidgetFieldSetting from './helpers/WidgetFieldSetting';
import { useTranslation } from 'Components/Helpers/translate';

export const EditUiWidgetIframe = ({ row, onUpdate }: EditWidgetRowProps) => {
	const { t } = useTranslation();
	const mode = row.settings?.mode || 'src';
	const url = row.settings?.url || '';
	const title = row.settings?.title || '';
	const srcdoc = row.settings?.srcdoc || '';

	const handleChange =
		(field: 'url' | 'title' | 'srcdoc') => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			onUpdate({
				...row,
				settings: {
					...row.settings,
					[field]: e.target.value
				}
			});
		};

	const handleModeChange = (value: 'src' | 'srcdoc') => {
		onUpdate({
			...row,
			settings: {
				...row.settings,
				mode: value
			}
		});
	};

	return (
		<Flex direction="column" gap="u2">
			<ButtonSelect
				className="UiWidgetIframe__modeSelect"
				value={mode}
				options={[
					{ value: 'src', title: 'Iframe URL' },
					{ value: 'srcdoc', title: 'HTML (srcdoc)' }
				]}
				onChange={handleModeChange}
			/>
			<WidgetFieldSetting text={t('default.title')}>
				<Input value={title} onChange={handleChange('title')} />
			</WidgetFieldSetting>
			{mode === 'src' ? (
				<WidgetFieldSetting text={t('default.url')}>
					<Input value={url} onChange={handleChange('url')} />
				</WidgetFieldSetting>
			) : (
				<WidgetFieldSetting text={t('default.srcdoc')}>
					<Textarea value={srcdoc} onChange={handleChange('srcdoc')} />
				</WidgetFieldSetting>
			)}
		</Flex>
	);
};

const UiWidgetIframe: React.FC<WidgetDataSourceProps> = ({ row, widgetExternalId, widgetType }) => {
	const src = row.settings?.url || '';
	const title = row.settings?.title || '';
	const mode = row.settings?.mode || 'src';
	const srcdoc = row.settings?.srcdoc || '';

	const iframeRef = useRef<HTMLIFrameElement>(null);

	// Add iframeMessage handler
	useEffect(() => {
		const iframeMessage = (event: MessageEvent) => {
			// Only process messages from our own iframe
			if (event.source !== iframeRef.current?.contentWindow) {
				return;
			}
			// Todo: pass more context here when implementing into the actual views
			handleIntegrationIframeMessage(widgetType || '', widgetExternalId || null, () => ({}), {}, event);
		};
		window.addEventListener('message', iframeMessage);
		return () => {
			window.removeEventListener('message', iframeMessage);
		};
	}, [widgetExternalId, widgetType]);

	return (
		<Flex className="UiWidgetIframe" direction="column">
			<Text className="UiWidgetIframe__title">{title}</Text>
			<div className="UiWidgetIframe__container">
				<iframe
					ref={iframeRef}
					src={mode === 'src' ? src : undefined}
					srcDoc={mode === 'srcdoc' ? srcdoc : undefined}
					title={title}
					className="UiWidgetIframe__iframe"
				/>
			</div>
		</Flex>
	);
};

export default UiWidgetIframe;
